import { useState } from 'react';
import { Alert, Button, Group, Paper, Stack, Stepper, Text, Textarea, TextInput, Title, Tooltip } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import type { StoresIssueWrite } from '@zebra/core';
import { apiFetch, ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { todayIsoDate } from '../../../lib/sastDate';
import { SiteSelect } from '../../../features/stores-transfers/components/SiteSelect';
import { CostCentreSelect } from '../../../features/stores-transfers/components/CostCentreSelect';
import { LineRowsEditor, emptyLineRow, type LineFormRow } from '../../../features/stores-transfers/components/LineRowsEditor';
import { LedgerPreview } from '../../../features/stores-transfers/components/LedgerPreview';
import { useCreateStoresIssue } from '../../../features/stores-transfers/api';

/** Owned by G24 (plan.md S0). Guided "create a stores issue" flow (BUILD_SPEC.md
 * §8.6): pick a cost centre -> add lines -> review with a ledger-effect preview ->
 * Post. */
export const Route = createFileRoute('/_authenticated/stores-transfers/issues/new')({
  component: NewIssuePage,
});

function NewIssuePage() {
  const canOperate = useCanOperate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createIssue = useCreateStoresIssue();

  const [active, setActive] = useState(0);
  const [siteId, setSiteId] = useState('');
  const [costCentreId, setCostCentreId] = useState('');
  const [costCentreLabel, setCostCentreLabel] = useState('');
  const [issuedDate, setIssuedDate] = useState(todayIsoDate());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineFormRow[]>([emptyLineRow()]);
  const [postError, setPostError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const detailsValid = Boolean(siteId && costCentreId && issuedDate);
  const linesValid = lines.length > 0 && lines.every((l) => l.productId && l.locationId && l.qty && Number(l.qty) > 0);

  function buildPayload(): StoresIssueWrite {
    return {
      siteId,
      costCentreId,
      issuedAt: new Date(issuedDate),
      notes: notes.trim() || null,
      lines: lines.map((l) => ({ productId: l.productId, fromLocationId: l.locationId, qty: l.qty })),
    };
  }

  async function handleSaveDraft() {
    setPostError(null);
    try {
      const created = await createIssue.mutateAsync(buildPayload());
      void navigate({ to: '/stores-transfers/issues/$issueId', params: { issueId: created.id } });
    } catch {
      // `createIssue.isError`/`.error` already drive the red text below.
    }
  }

  async function handlePost() {
    setPostError(null);
    setPosting(true);
    try {
      const created = await createIssue.mutateAsync(buildPayload());
      try {
        await apiFetch(`/stores/issues/${created.id}/post`, { method: 'POST' });
        await queryClient.invalidateQueries({ queryKey: ['stores', 'issues'] });
      } catch (err) {
        // The DRAFT was created successfully even though the immediate Post failed
        // (e.g. a transient network error) — send the user to the issue's own detail
        // page, where DRAFT documents show a Post button so they can retry, rather
        // than losing the draft they just entered.
        const message = err instanceof ApiRequestError ? err.message : 'Posting failed unexpectedly.';
        setPostError(`Draft ${created.number} was saved, but posting failed: ${message} You can retry from the issue.`);
      }
      void navigate({ to: '/stores-transfers/issues/$issueId', params: { issueId: created.id } });
    } catch {
      // Creation itself failed (e.g. validation) — `createIssue.isError`/`.error`
      // already drive the red text below; nothing further to do here beyond not
      // leaving an unhandled rejection.
    } finally {
      setPosting(false);
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Stack gap={2}>
        <Title order={2}>New stores issue</Title>
        <Text c="dimmed" size="sm">
          Issue stock against a cost centre — spares, consumables, or general usage.
        </Text>
      </Stack>

      {postError && (
        <Alert color="amber" icon={<IconAlertTriangle size={18} />} title="Partial success">
          {postError}
        </Alert>
      )}

      <Stepper active={active} onStepClick={setActive}>
        <Stepper.Step label="Details" description="Site &amp; cost centre">
          <Paper withBorder radius="md" p="md" mt="md">
            <Stack gap="md">
              <SiteSelect
                label="Site"
                value={siteId}
                onChange={(id) => {
                  setSiteId(id);
                  setCostCentreId('');
                  setCostCentreLabel('');
                }}
                required
              />
              <CostCentreSelect
                label="Cost centre"
                description="The part of the plant this spend is charged to — e.g. Dryer, PM1, Site General."
                siteId={siteId || undefined}
                value={costCentreId}
                onChange={(id, label) => {
                  setCostCentreId(id);
                  setCostCentreLabel(label);
                }}
                required
              />
              <TextInput
                type="date"
                label="Issued date"
                value={issuedDate}
                onChange={(event) => setIssuedDate(event.currentTarget.value)}
                required
              />
              <Textarea label="Notes" value={notes} onChange={(event) => setNotes(event.currentTarget.value)} autosize minRows={2} />
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Lines" description="What to issue">
          <Paper withBorder radius="md" p="md" mt="md">
            <LineRowsEditor rows={lines} onChange={setLines} siteId={siteId || undefined} includeLocation locationLabel="From location" />
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Review" description="Ledger effect &amp; Post">
          <Paper withBorder radius="md" p="md" mt="md">
            <LedgerPreview siteId={siteId || undefined} lines={lines} attribution={`Cost centre: ${costCentreLabel}`} />
          </Paper>
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between">
        <Button variant="default" onClick={() => setActive((s) => Math.max(0, s - 1))} disabled={active === 0}>
          Back
        </Button>
        {active < 2 ? (
          <Button
            onClick={() => setActive((s) => Math.min(2, s + 1))}
            disabled={(active === 0 && !detailsValid) || (active === 1 && !linesValid)}
          >
            Continue
          </Button>
        ) : (
          <Group>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button variant="default" disabled={!canOperate} onClick={handleSaveDraft} loading={createIssue.isPending && !posting}>
                  Save as draft
                </Button>
              </span>
            </Tooltip>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button onClick={handlePost} loading={posting} disabled={!canOperate || !detailsValid || !linesValid}>
                  Post
                </Button>
              </span>
            </Tooltip>
          </Group>
        )}
      </Group>
      {(createIssue.isError && !postError) && (
        <Text c="red" size="sm">
          {createIssue.error instanceof Error ? createIssue.error.message : 'Something went wrong creating the issue.'}
        </Text>
      )}
    </Stack>
  );
}
